import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Input,
  InputGroup,
  Table,
  DOMHelper,
  Stack,
  Panel,
  IconButton,
  Pagination,
  Message,
  useToaster,
  Button,
  Checkbox,
  Popover,
  Whisper
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import MoreIcon from '@rsuite/icons/legacy/More';
import { Edit, Icon } from '@rsuite/icons';
import { MdOutlineDeleteOutline, MdPersonAdd, MdDelete } from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import {
  deleteIndividualAccess,
  getIndividualAccess,
  getZonePrioritiesWithUsers
} from '@/services/access.service';
import { fetchSetting } from '@/services/setting.service';
import { getUsers } from '@/services/user.service';
import { getFloors } from '@/services/floor.service';
import { getZones, removeAccessess } from '@/services/zone.service';
import ConfirmModal from '@/components/ConfirmModal';
import FilterDrawer from './FilterDrawer';
import AddAccessDrawer from './AddDrawer';
import AddAccessPrioDrawer from './AddPrio';
import EditDrawer from './EditDrawer';
import EditPrioDrawer from './EditPrio';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const DataTable = () => {
  const { t } = useTranslation();
  const toaster = useToaster();
  const dispatch = useAppDispatch();

  const [showDrawer, setShowDrawer] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState({ open: false, data: null });
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [withPriority, setWithPriority] = useState(false);
  const zonesStore = useAppSelector((state: any) => state.zone?.data);
  const floors = useAppSelector((state: any) => state.floor?.data);
  const users = useAppSelector((state: any) => state.users.data);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    subtitle: '',
    onConfirm: () => {}
  });
  const [checkedKeys, setCheckedKeys] = useState([]);
  const individualAccess = useAppSelector((state: any) => state.access.data);
  const zonePrioritiesWithUsers = useAppSelector(
    (state: any) => state.access.zonePrioritiesWithUsers
  );
  const loading = useAppSelector((state: any) => state.access.loading);
  const [filters, setFilters] = useState(
    withPriority ? { user: '', zone: '', priority: '' } : { user: '', zones: [] }
  );

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters(withPriority ? { user: '', zone: '', priority: '' } : { user: '', zones: [] });
    setPage(1);
  };

  useEffect(() => {
    const fetchPrioritySetting = async () => {
      try {
        const res: any = await fetchSetting('Homepage');
        setWithPriority(res?.configuration?.bookingWithPriority);
      } catch (e) {
        console.error('Error fetching priority setting:', e);
      }
    };
    fetchPrioritySetting();
  }, []);

  useEffect(() => {
    dispatch(getIndividualAccess());
    if (withPriority) {
      dispatch(getZonePrioritiesWithUsers());
    }
  }, [dispatch, withPriority]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getZones());
      await dispatch(getUsers());
      await dispatch(getFloors());
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    setFilters(withPriority ? { user: '', zone: '', priority: '' } : { user: '', zones: [] });
  }, [withPriority]);

  const handleSortColumn = (sortColumn, sortType) => {
    setSortColumn(sortColumn);
    setSortType(sortType);
  };

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const groupDataByUser = data => {
    if (!data || !Array.isArray(data)) return [];

    const userMap = new Map();

    data.forEach(item => {
      if (!item.user || !item.user._id) return;

      const userId = item.user._id;

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          _id: `group_${userId}`,
          user: { ...item.user },
          accessEntries: [item]
        });
      } else {
        userMap.get(userId).accessEntries.push(item);
      }
    });

    return Array.from(userMap.values());
  };

  const filteredData = () => {
    let filtered = withPriority ? [...zonePrioritiesWithUsers] : [...individualAccess];

    if (searchKeyword) {
      const keyword = searchKeyword.toLowerCase().trim();
      filtered = filtered?.filter(item => {
        let userFullName = '';
        if (withPriority) {
          userFullName = `${item.user?.firstname || ''} ${item.user?.lastname || ''}`.toLowerCase();
        } else {
          userFullName = `${item.User?.firstname || ''} ${item.User?.lastname || ''}`.toLowerCase();
        }
        const userMatch = userFullName.includes(keyword);

        let zoneMatch = false;
        if (withPriority) {
          zoneMatch = (item.zone?.name || '').toLowerCase().includes(keyword);
        } else {
          zoneMatch = item.Zones?.some(zone => zone.name.toLowerCase().includes(keyword));
        }

        return userMatch || zoneMatch;
      });
    }

    if (filters.user && filters.user !== '') {
      filtered = filtered?.filter(item => {
        const userId = withPriority ? item.user?._id : item.User?._id;
        return userId === filters.user;
      });
    }

    if (withPriority) {
      if (filters.zone && filters.zone !== '') {
        filtered = filtered?.filter(item => item.zone?._id === filters.zone);
      }

      if (filters.priority !== undefined && filters.priority !== '' && filters.priority !== null) {
        const priorityNum = Number(filters.priority);
        filtered = filtered?.filter(item => item.priority === priorityNum);
      }

      if (
        filters.dayOfWeek !== undefined &&
        filters.dayOfWeek !== '' &&
        filters.dayOfWeek !== null
      ) {
        const dayNum = Number(filters.dayOfWeek);
        filtered = filtered?.filter(item => item.dayOfWeek === dayNum);
      }
    } else {
      if (filters.zones && filters.zones.length > 0) {
        filtered = filtered?.filter(item =>
          filters.zones.every(zoneId => item.Zones?.some(zone => zone._id === zoneId))
        );
      }
    }

    if (withPriority) {
      filtered = groupDataByUser(filtered);
    }

    if (sortColumn && sortType) {
      filtered = [...filtered].sort((a, b) => {
        let x;
        let y;

        if (sortColumn === 'user') {
          if (withPriority) {
            x = `${a.user?.firstname || ''} ${a.user?.lastname || ''}`;
            y = `${b.user?.firstname || ''} ${b.user?.lastname || ''}`;
          } else {
            x = `${a.User?.firstname || ''} ${a.User?.lastname || ''}`;
            y = `${b.User?.firstname || ''} ${b.User?.lastname || ''}`;
          }
        } else if (sortColumn === 'zones' && !withPriority) {
          x = a.Zones?.map(zone => zone.name).join(', ') || '';
          y = b.Zones?.map(zone => zone.name).join(', ') || '';
        } else if (sortColumn === 'zone' && withPriority) {
          if (a.accessEntries) {
            x = '';
          } else {
            x = a.zone?.name || '';
          }

          if (b.accessEntries) {
            y = '';
          } else {
            y = b.zone?.name || '';
          }
        } else {
          x = a[sortColumn];
          y = b[sortColumn];
        }

        if (typeof x === 'string') {
          x = x.toLowerCase();
          y = y.toLowerCase();
        }

        if (x === null || x === undefined) return 1;
        if (y === null || y === undefined) return -1;

        if (sortType === 'asc') {
          return x > y ? 1 : -1;
        } else {
          return x < y ? 1 : -1;
        }
      });
    }

    const total = filtered.length;
    const start = (page - 1) * limit;
    const end = start + limit;
    filtered = filtered.slice(start, end);

    return {
      data: filtered,
      total: total
    };
  };

  const handleDelete = async rowData => {
    try {
      console.log('Row data for deletion:', rowData);
      if (!rowData._id) {
        throw new Error('Access ID is missing');
      }
      setConfirmModal({
        open: true,
        title: t('Are you sure to delete this entire access entry?'),
        subtitle: t('This will remove all zone accesses for this user'),
        onConfirm: async () => {
          console.log('Deleting access with ID:', rowData._id);
          await dispatch(deleteIndividualAccess(rowData._id));
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Individual access deleted successfully')}
            </Message>,
            { placement: 'topCenter' }
          );

          setConfirmModal({ open: false });
        }
      });
    } catch (error) {
      console.error('Delete error:', error);
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t(error.message) || t('Error deleting access')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };

  const handleCheckAll = (value, checked) => {
    if (withPriority) {
      const allEntryIds = filteredData().data.flatMap(group =>
        group.accessEntries ? group.accessEntries.map(entry => entry._id) : [group._id]
      );
      setCheckedKeys(checked ? allEntryIds : []);
    } else {
      const keys = checked ? individualAccess.map(item => item._id) : [];
      setCheckedKeys(keys);
    }
  };

  const handleCheck = (value, checked) => {
    const keys = checked ? [...checkedKeys, value] : checkedKeys.filter(item => item !== value);
    setCheckedKeys(keys);
  };

  const handleGroupCheck = (entries, checked) => {
    const entryIds = entries.map(entry => entry._id);
    let newCheckedKeys;

    if (checked) {
      newCheckedKeys = [...new Set([...checkedKeys, ...entryIds])];
    } else {
      newCheckedKeys = checkedKeys.filter(key => !entryIds.includes(key));
    }

    setCheckedKeys(newCheckedKeys);
  };

  const handleBulkDelete = () => {
    setConfirmModal({
      open: true,
      title: t('Are you sure to delete all the zone priorities of these individual access?'),
      subtitle: t('You cannot undo this operation'),
      onConfirm: () => {
        console.log('Deleting access for IDs:', checkedKeys);
        dispatch(removeAccessess(checkedKeys))
          .then(() => {
            setCheckedKeys([]);
            toaster.push(
              <Message type="success" closable>
                {t('Zone priorities were successfully deleted.')}
              </Message>
            );
            dispatch(getZonePrioritiesWithUsers());
            setConfirmModal({ open: false });
          })
          .catch(error => {
            console.error('Error deleting zone priorities:', error);
            toaster.push(
              <Message type="error" closable>
                {t('Failed to delete zone priorities.')}
              </Message>
            );
          });
      }
    });
  };

  const handleAddAccess = () => {
    setShowAddDrawer(true);
  };

  // Define translatable dayOfWeekMap
  const dayOfWeekMap = {
    0: t('Sunday'),
    1: t('Monday'),
    2: t('Tuesday'),
    3: t('Wednesday'),
    4: t('Thursday'),
    5: t('Friday'),
    6: t('Saturday')
  };

  const NameCell = ({ rowData, dataKey, ...props }) => {
    if (rowData.accessEntries && rowData.accessEntries.length > 0) {
      const speakerContent = (
        <div>
          <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>{t('Access Details')}:</h6>
          {rowData.accessEntries.map((entry, index) => (
            <div
              key={entry._id || index}
              style={{
                marginBottom: '10px',
                borderBottom: index < rowData.accessEntries.length - 1 ? '1px solid #ddd' : 'none',
                paddingBottom: '5px'
              }}
            >
              <p>
                <b>{t('Floor')}:</b> {entry.zone?.floor?.name || t('Not set')}
              </p>
              <p>
                <b>{t('Zone')}:</b> {entry.zone?.name || t('Not assigned')}
              </p>
              <p>
                <b>{t('Day')}:</b> {dayOfWeekMap[entry.dayOfWeek] || t('Not set')}
              </p>
              <p>
                <b>{t('Priority')}:</b>{' '}
                {entry.priority !== undefined ? entry.priority : t('Not set')}
              </p>
            </div>
          ))}
        </div>
      );

      return (
        <Cell {...props}>
          <Whisper
            placement="autoVertical"
            trigger="hover"
            speaker={
              <Popover title={`${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`}>
                {speakerContent}
              </Popover>
            }
          >
            <a>
              {`${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`} (
              {rowData.accessEntries.length})
            </a>
          </Whisper>
        </Cell>
      );
    } else {
      const speaker = (
        <Popover title={`${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`}>
          <p>
            <b>{t('Floor')}:</b> {rowData.zone?.floor?.name || t('Not set')}
          </p>
          <p>
            <b>{t('Zone')}:</b> {rowData.zone?.name || t('Not assigned')}
          </p>
          <p>
            <b>{t('Day')}:</b> {dayOfWeekMap[rowData.dayOfWeek] || t('Not set')}
          </p>
          <p>
            <b>{t('Priority')}:</b>{' '}
            {rowData.priority !== undefined ? rowData.priority : t('Not set')}
          </p>
        </Popover>
      );

      return (
        <Cell {...props}>
          <Whisper placement="auto" speaker={speaker}>
            <a>{`${rowData.user?.firstname || ''} ${rowData.user?.lastname || ''}`}</a>
          </Whisper>
        </Cell>
      );
    }
  };

  const renderPriorityTable = () => (
    <Table
      height={Math.max(getHeight(window) - 250, 400)}
      data={filteredData().data}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={handleSortColumn}
      loading={loading}
    >
      <Column width={50}>
        <HeaderCell style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            inline
            checked={
              checkedKeys.length > 0 &&
              filteredData().data.every(group =>
                group.accessEntries
                  ? group.accessEntries.every(entry => checkedKeys.includes(entry._id))
                  : checkedKeys.includes(group._id)
              )
            }
            indeterminate={
              checkedKeys.length > 0 &&
              !filteredData().data.every(group =>
                group.accessEntries
                  ? group.accessEntries.every(entry => checkedKeys.includes(entry._id))
                  : checkedKeys.includes(group._id)
              )
            }
            onChange={handleCheckAll}
          />
        </HeaderCell>
        <Cell style={{ display: 'flex', alignItems: 'center' }}>
          {rowData => {
            if (rowData.accessEntries) {
              const entryIds = rowData.accessEntries.map(entry => entry._id);
              const allChecked = entryIds.every(id => checkedKeys.includes(id));
              const someChecked = entryIds.some(id => checkedKeys.includes(id));

              return (
                <Checkbox
                  inline
                  checked={allChecked}
                  indeterminate={someChecked && !allChecked}
                  onChange={(value, checked) => handleGroupCheck(rowData.accessEntries, checked)}
                />
              );
            } else {
              return (
                <Checkbox
                  inline
                  checked={checkedKeys.includes(rowData._id)}
                  onChange={(value, checked) => handleCheck(rowData._id, checked)}
                />
              );
            }
          }}
        </Cell>
      </Column>
      <Column flexGrow={2} width={500} sortable resizable>
        <HeaderCell>{t('Name')}</HeaderCell>
        <NameCell dataKey="user" />
      </Column>
      <Column flexGrow={0.5} fixed="right">
        <HeaderCell>
          <MoreIcon />
        </HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {rowData => (
            <Stack>
              <IconButton
                onClick={() => {
                  if (rowData.accessEntries) {
                    setShowEditDrawer({
                      open: true,
                      data: rowData.accessEntries
                    });
                  } else {
                    setShowEditDrawer({
                      open: true,
                      data: rowData
                    });
                  }
                }}
                size="xs"
                icon={<Edit color="blue" />}
                id={`edit-${rowData._id}`}
              />
              <IconButton
                size="xs"
                icon={<MdOutlineDeleteOutline color="red" />}
                onClick={() => {
                  if (rowData.accessEntries) {
                    const entryIds = rowData.accessEntries.map(entry => entry._id);
                    setConfirmModal({
                      open: true,
                      title: t('Are you sure to remove all priorities for this user?'),
                      subtitle: t('You cannot undo this operation'),
                      onConfirm: () => {
                        console.log('Deleting priorities with IDs:', entryIds);
                        dispatch(removeAccessess(entryIds))
                          .then(() => {
                            toaster.push(
                              <Message type="success" closable>
                                {t('All zone priorities for this user were successfully deleted.')}
                              </Message>
                            );
                            dispatch(getZonePrioritiesWithUsers());
                            setConfirmModal({ open: false });
                          })
                          .catch(error => {
                            console.error('Error deleting zone priorities:', error);
                            toaster.push(
                              <Message type="error" closable>
                                {t('Failed to delete zone priorities.')}
                              </Message>
                            );
                          });
                      }
                    });
                  } else {
                    setConfirmModal({
                      open: true,
                      title: t('Are you sure to remove this priority?'),
                      subtitle: t('You cannot undo this operation'),
                      onConfirm: () => {
                        console.log('Deleting priority with ID:', rowData._id);
                        dispatch(removeAccessess([rowData._id]))
                          .then(() => {
                            toaster.push(
                              <Message type="success" closable>
                                {t('Zone priority was successfully deleted.')}
                              </Message>
                            );
                            dispatch(getZonePrioritiesWithUsers());
                            setConfirmModal({ open: false });
                          })
                          .catch(error => {
                            console.error('Error deleting zone priority:', error);
                            toaster.push(
                              <Message type="error" closable>
                                {t('Failed to delete zone priority.')}
                              </Message>
                            );
                          });
                      }
                    });
                  }
                }}
                id={`delete-${rowData._id}`}
              />
            </Stack>
          )}
        </Cell>
      </Column>
    </Table>
  );

  const renderNormalTable = () => (
    <Table
      height={Math.max(getHeight(window) - 250, 400)}
      data={filteredData().data}
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={handleSortColumn}
      loading={loading}
    >
      <Column width={300} resizable>
        <HeaderCell>{t('User')}</HeaderCell>
        <Cell>{rowData => `${rowData.User?.firstname || ''} ${rowData.User?.lastname || ''}`}</Cell>
      </Column>

      <Column width={250} flexGrow={1} resizable>
        <HeaderCell>{t('Zones')}</HeaderCell>
        <Cell style={{ whiteSpace: 'pre-line' }}>
          {rowData => rowData.Zones?.map(zone => `${zone.name}, `).join('')}
        </Cell>
      </Column>

      <Column width={50} flexGrow={0.5} fixed="right">
        <HeaderCell>
          <MoreIcon />
        </HeaderCell>
        <Cell style={{ padding: '6px' }}>
          {rowData => (
            <Stack>
              <IconButton
                onClick={() => {
                  setShowEditDrawer({
                    open: true,
                    data: rowData
                  });
                }}
                size="xs"
                icon={<Edit color="blue" />}
                id={`edit-${rowData._id}`}
              />
              <IconButton
                size="xs"
                icon={<MdOutlineDeleteOutline color="red" />}
                onClick={() => {
                  setConfirmModal({
                    open: true,
                    title: t('Are you sure to remove this individual access?'),
                    subtitle: t('You cannot undo this operation'),
                    onConfirm: async () => {
                      console.log('Deleting access with ID:', rowData._id);
                      await handleDelete(rowData);
                    }
                  });
                }}
                id={`delete-${rowData._id}`}
              />
            </Stack>
          )}
        </Cell>
      </Column>
    </Table>
  );

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <InputGroup inside>
            <Input placeholder={t('Search')} value={searchKeyword} onChange={handleSearch} />
            <InputGroup.Addon>
              <SearchIcon />
            </InputGroup.Addon>
          </InputGroup>
        </Stack>
        <Stack spacing={6}>
          {withPriority && checkedKeys.length > 0 && (
            <IconButton
              appearance="primary"
              color="red"
              icon={<Icon as={MdDelete} />}
              onClick={handleBulkDelete}
            >
              {t('Delete Priorities')}
            </IconButton>
          )}

          {!(withPriority && checkedKeys.length > 0) && (
            <>
              <IconButton
                appearance="primary"
                icon={<Icon as={MdPersonAdd} />}
                onClick={handleAddAccess}
              >
                {t('Add Individual Access')}
              </IconButton>

              <Button appearance="primary" onClick={() => setShowDrawer(true)}>
                {t('Filters')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <Panel bodyFill>
        {withPriority ? renderPriorityTable() : renderNormalTable()}
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData().total}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </Panel>
      <FilterDrawer
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        withPriority={withPriority}
        users={users}
        zones={zonesStore}
      />

      {withPriority ? (
        <AddAccessPrioDrawer
          open={showAddDrawer}
          onClose={() => setShowAddDrawer(false)}
          users={users}
          withPriority={withPriority}
        />
      ) : (
        <AddAccessDrawer
          open={showAddDrawer}
          onClose={() => setShowAddDrawer(false)}
          users={users}
          withPriority={withPriority}
        />
      )}

      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModal} />
      )}
      {withPriority ? (
        <EditPrioDrawer
          open={showEditDrawer.open}
          onClose={() => setShowEditDrawer({ open: false, data: null })}
          users={users}
          zones={zonesStore}
          floors={floors}
          initialData={{
            user:
              Array.isArray(showEditDrawer.data) && showEditDrawer.data.length > 0
                ? showEditDrawer.data[0].user
                : showEditDrawer.data?.user,
            priorities: Array.isArray(showEditDrawer.data)
              ? showEditDrawer.data.map(item => ({
                  dayOfWeek: item.dayOfWeek,
                  priority: item.priority,
                  zone: item.zone?._id || item.zone,
                  _id: item._id
                }))
              : [showEditDrawer.data]
                  .filter(item => item && item._id)
                  .map(item => ({
                    dayOfWeek: item.dayOfWeek,
                    priority: item.priority,
                    zone: item.zone?._id || item.zone,
                    _id: item._id
                  }))
          }}
          isEdit={true}
        />
      ) : (
        <EditDrawer
          open={showEditDrawer.open}
          onClose={() => setShowEditDrawer({ open: false, data: null })}
          data={showEditDrawer.data}
          floors={floors}
          zones={zonesStore}
        />
      )}
    </>
  );
};

export default DataTable;
